/**
 * membership-input.component.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { Component, EventEmitter, Input, Output } from "@angular/core";
import { FormControl } from "@angular/forms";
import { Displayable } from "../types/aliases";
import { dot } from "../utilities/functions";

@Component({
    selector: "membership-input",
    template: `
<div class="row">
    <div class="col-sm-5">
        <select
            [size]="size()"
            class="form-control"
            [formControl]="selectedOption">
            <option *ngIf="!options?.length" disabled>
                (empty)
            </option>
            <option
                *ngFor="let item of options"
                [ngValue]="item"
                [class]="item.membershipClass"
                [disabled]="item.membershipDisabled"
                (dblclick)="add()">
                {{item.displayName}}
            </option>
        </select>
    </div>
    <div class="col-sm-2 button-col">
        <button class="btn btn-xs btn-default" (click)="add()">
            →
        </button>
        <button class="btn btn-xs btn-default" (click)="remove()">
            ←
        </button>
        <button
            class="btn btn-xs btn-default"
            (click)="reset()">
            Reset
        </button>
    </div>
    <div class="col-sm-5">
        <select
            [size]="size()"
            class="form-control"
            [formControl]="selectedTarget">
            <option *ngIf="!target?.length" disabled>
                (empty)
            </option>
            <option
                *ngFor="let item of target"
                [ngValue]="item"
                [class]="item.membershipClass"
                [disabled]="item.membershipDisabled"
                (dblclick)="remove()">
                {{item.displayName}}
            </option>
        </select>
    </div>
</div>
    `,
    styles: [`
        .button-col {
            padding-left: 0;
            padding-right: 0;
        }
        .button-col button {
            width: 100%;
        }
        option.special1 {
            font-style: italic;
            color: var(--color-blue);
        }
        option.special2 {
            font-style: italic;
            color: var(--color-green);
        }
    `]
})
export class MembershipInputComponent
{
    /**
     * The incoming interface for setting the available choices for membership.
     */
    @Input()
    public set source (source : Displayable[])
    {
        if (this._lastSource === source)
        {
            return;
        }
        if (Array.isArray(source))
        {
            this._source = source;
            this._lastSource = source;
        }
        else
        {
            console.error("Membership input received non-array source");
            this._source = [];
            this._lastSource = [];
        }
        this.reset();
    }

    /**
     * Whether the output should preserve order or not.
     */
    @Input()
    public ordered : boolean = true;

    @Input()
    public optionsSorted : boolean = true;

    protected sortOptions () : void
    {
        if (this.optionsSorted)
        {
            this.options.sort((a : Displayable, b : Displayable) => {
                if (!a || (typeof a.displayName !== "string"))
                {
                    return -1;
                }
                else if (!b || (typeof b.displayName !== "string"))
                {
                    return 1;
                }
                else
                {
                    return a.displayName.localeCompare(b.displayName);
                }
            });
        }
    }

    /**
     * The original array of options for set membership. This isn't used by the
     * UI directly so it can be preserved for resets.
     */
    protected _source : Displayable[] = [];
    protected _lastSource : Displayable[] = [];

    /**
     * The current slew of available options, *NOT* including those that have
     * already been selected. (i.e. the left column)
     */
    public options : Displayable[] = [];

    public selectedOption : FormControl = new FormControl();
    public selectedTarget : FormControl = new FormControl();

    /**
     * The current set of items selected for membership. (i.e. the right column)
     * This supposes a model where the array reference is passed directly to the
     * component and *requires* that other classes treat the array as immutable
     * in order to preserve the relationship between target and options.
     *
     * If an external service wants to modify the target (as in a submit-like
     * action), it should call reset() and use the output.
     */
    @Input()
    public target : any[] = [];

    /**
     * Alternative access paradigm for the target array, which emits events
     * whenever its contents change. This prevents modifications, but requires
     * much more boilerplate to use (and effectively a local cache of the last
     * received value).
     */
    @Output()
    public change : EventEmitter<any> = new EventEmitter();

    /**
     * Clear the selections and reset the available choices to the original
     * array of options.
     */
    public reset () : any[]
    {
        const out : any[] = this.target;
        this.options = Array.from(this._source);
        this.sortOptions();
        this.target.length = 0; // yes, this apparently will clear the array
        this.change.emit(this.target);
        this.selectedTarget.reset();
        this.selectedOption.reset();
        return out;
    }

    @Input()
    public max : number = 20;
    protected readonly min : number = 3;

    /**
     * Return the size of the select boxes, which should be the larger of the
     * two arrays, down to a minimum to keep the buttons in a cohesive layout.
     */
    public size () : number
    {
        return Math.min(
            Math.max(this._source.length, this.target.length, this.min),
            this.max
        );
    }

    /**
     * Remove a value from options and add it to target.
     */
    public add () : void
    {
        const item : any = this.selectedOption.value;
        if (item === null)
        {
            return;
        }
        const i : number = this.options.indexOf(item);
        if (i === -1)
        {
            throw new Error(`Could not find item in options: ${item}`);
        }

        this.target.push(this.options.splice(i, 1)[0]);
        this.selectedOption.reset();
        this.change.emit(this.target);
    }

    /**
     * Remove a value from target and return it to options.
     */
    public remove () : void
    {
        const item : any = this.selectedTarget.value;
        if (item === null)
        {
            return;
        }
        const i : number = this.target.indexOf(item);
        if (i === -1)
        {
            throw new Error(`Could not find item in options: ${item}`);
        }

        this.options.push(this.target.splice(i, 1)[0]);
        this.sortOptions();
        this.selectedTarget.reset();
        this.change.emit(this.target);
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/support/controls/membership-input.component.ts